import { authFetchAsync } from "utils/auth/authFetch";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"; // PayloadAction
import { RootState } from "../../store";

export type RoundingRuleConfig = {
    version: string,
    endingDigit: string[] | undefined, // [0,5,9]
    decimalPoint: "0" | "1" | "2" | undefined, // 2
    roundingType: "regular" | "up" | "down" | undefined // regular
}

type InitialState = {
    loading: boolean,
    error: string,
    roundingRuleConfig: RoundingRuleConfig
}

const initialState: InitialState = {
    loading: true,
    error: "",
    roundingRuleConfig: {
        version: "",
        endingDigit: [],
        decimalPoint: undefined,
        roundingType: undefined
    },
}

// Generates pending, fulfilled and rejected action types
export const fetchRoundingRuleConfig = createAsyncThunk(
    "user/fetchRoundingRuleConfig",
    async (_, store) => {
        const coutryCode = (store.getState() as RootState).multiMarket.marketData.selectedMarket;
        return authFetchAsync(`api/${coutryCode}/Rules`,"GET")
            .then(response => response.json())
            .then(data => {
                return {
                    version: data.version,
                    endingDigit: data.endingDigit.split(","),
                    decimalPoint: String(data.decimalPoint),
                    roundingType: data.roundingType
                }
            });
    }
);

// Generates pending, fulfilled and rejected action types
export const saveRoundingRuleConfig = createAsyncThunk(
    "user/saveRoundingRuleConfig",
    async (newConfigData:RoundingRuleConfig, store) => {
        const coutryCode = (store.getState() as RootState).multiMarket.marketData.selectedMarket;

        const data = {
            ...newConfigData,
            endingDigit: newConfigData.endingDigit?.toString(),
            countryCode: coutryCode
        }

        return authFetchAsync(`api/${coutryCode}/Rules`,"POST", data)
            .then((response) => {
                if(response.status !== 200) {
                    throw response.status;
                }
                return newConfigData;
            });
    }
);

const userSlice = createSlice({
    name: "roundingRule",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        // Fetching Config
        builder.addCase(fetchRoundingRuleConfig.pending, state => {
            state.loading = true;
        });
        builder.addCase(fetchRoundingRuleConfig.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "Something went wrong";
        });
        builder.addCase(
            fetchRoundingRuleConfig.fulfilled,
            (state: InitialState, action: any) => {
                state.loading = false;
                state.error = "";
                state.roundingRuleConfig = action.payload || {
                    endingDigit: ["0","5","9"],
                    decimalPoint: "2",
                    roundingType: "up"
                };
            }
        );

        // Saving Config
        builder.addCase(saveRoundingRuleConfig.pending, state => {
            state.loading = true;
        });
        builder.addCase(saveRoundingRuleConfig.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "Something went wrong";
        });
        builder.addCase(
            saveRoundingRuleConfig.fulfilled,
            (state: InitialState, action: any) => {
                state.loading = false;
                state.error = "";
                state.roundingRuleConfig = action.payload;
            }
        );
    }
})

export default userSlice.reducer;
